import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, LayoutDashboard, Inbox, Wallet, Dog, PenSquare, Loader2, Users, CalendarDays, BarChart3, Settings, UserCog, Newspaper, Share2, GraduationCap } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import EnquiriesTab from "./EnquiriesTab";
import BillingTab from "./BillingTab";
import ClientsTab from "./ClientsTab";
import EventsTab from "./EventsTab";
import MonthlyTab from "./MonthlyTab";
import SettingsTab from "./SettingsTab";
import UsersTab from "./UsersTab";
import DogsTab from "./DogsTab";
import ContentTab from "./ContentTab";
import BlogTab from "./BlogTab";
import SocialTab from "./SocialTab";
import TrainingTab from "./TrainingTab";

const StatCard = ({ label, value, sub, testid }) => (
  <div className="rounded-lg border border-brand-line bg-brand-surface p-6" data-testid={testid}>
    <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold text-white">{value}</p>
    {sub && <p className="mt-1 text-xs text-brand-crimson">{sub}</p>}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [trainingDog, setTrainingDog] = useState(null);
  const [focusEnquiryId, setFocusEnquiryId] = useState(null);

  const loadOverview = useCallback(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
    api
      .get("/enquiries")
      .then((r) => setRecent((r.data || []).slice(0, 5)))
      .catch(() => setRecent([]));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  // Always show live numbers when the Overview tab is (re)opened
  useEffect(() => {
    if (activeTab === "overview") loadOverview();
  }, [activeTab, loadOverview]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <main className="min-h-screen bg-brand-obsidian text-white" data-testid="admin-dashboard">
      <header className="border-b border-brand-line bg-brand-surface/60 backdrop-blur sticky top-0 z-40">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Quest K9's" className="h-12 w-12 object-contain drop-shadow-[0_0_12px_rgba(200,16,46,0.4)]" />
            <div className="leading-none">
              <p className="font-display font-bold">Control Room</p>
              <p className="text-[10px] text-white/50 mt-1">Signed in as {user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="inline-flex items-center gap-1.5 text-xs font-eyebrow uppercase tracking-[0.15em] text-white/60 hover:text-white transition-colors"
            >
              View Site <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <button
              onClick={doLogout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-1.5 text-xs font-eyebrow uppercase tracking-[0.15em] text-white/60 hover:text-brand-crimson transition-colors"
            >
              Logout <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-tabs-container">
          <TabsList className="bg-brand-surface border border-brand-line flex-wrap h-auto gap-1 justify-start" data-testid="admin-tabs">
            <TabsTrigger value="overview" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-overview">
              <LayoutDashboard className="h-4 w-4 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-enquiries">
              <Inbox className="h-4 w-4 mr-1.5" /> Enquiries
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-invoices">
              <Wallet className="h-4 w-4 mr-1.5" /> Invoices & Quotes
            </TabsTrigger>
            <TabsTrigger value="clients" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-clients">
              <Users className="h-4 w-4 mr-1.5" /> Clients
            </TabsTrigger>
            <TabsTrigger value="calendar" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-calendar">
              <CalendarDays className="h-4 w-4 mr-1.5" /> Calendar
            </TabsTrigger>
            <TabsTrigger value="monthly" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-monthly">
              <BarChart3 className="h-4 w-4 mr-1.5" /> Monthly
            </TabsTrigger>
            <TabsTrigger value="dogs" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-dogs">
              <Dog className="h-4 w-4 mr-1.5" /> Dogs
            </TabsTrigger>
            <TabsTrigger value="training" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-training">
              <GraduationCap className="h-4 w-4 mr-1.5" /> Training
            </TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-content">
              <PenSquare className="h-4 w-4 mr-1.5" /> Content
            </TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-blog">
              <Newspaper className="h-4 w-4 mr-1.5" /> Blog
            </TabsTrigger>
            <TabsTrigger value="social" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-social">
              <Share2 className="h-4 w-4 mr-1.5" /> Social Media
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-settings">
              <Settings className="h-4 w-4 mr-1.5" /> Settings
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-brand-crimson data-[state=active]:text-white" data-testid="admin-tab-users">
              <UserCog className="h-4 w-4 mr-1.5" /> Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-8" data-testid="admin-overview">
            {!stats ? (
              <div className="flex justify-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="stats-loading-spinner" />
              </div>
            ) : (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <StatCard label="New Enquiries" value={stats.new_enquiries} sub={`${stats.total_enquiries} total`} testid="stat-new-enquiries" />
                  <StatCard label="Unpaid Invoices" value={stats.unpaid_invoices} sub={`£${(stats.unpaid_value || 0).toLocaleString()} outstanding`} testid="stat-unpaid-invoices" />
                  <StatCard label="K9 Teams" value={stats.total_dogs} sub="ready to deploy" testid="stat-total-dogs" />
                  <StatCard label="Site Status" value="Live" sub="content editable below" testid="stat-site-status" />
                </div>
                <div className="mt-8 rounded-lg border border-brand-line bg-brand-surface p-6" data-testid="recent-enquiries-panel">
                  <p className="font-eyebrow text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold mb-4">Latest Enquiries</p>
                  {recent.length === 0 ? (
                    <p className="text-sm text-white/50">No enquiries yet.</p>
                  ) : (
                    <ul className="divide-y divide-brand-line">
                      {recent.map((e) => (
                        <li
                          key={e.id}
                          onClick={() => { setFocusEnquiryId(e.id); setActiveTab("enquiries"); }}
                          className="py-3 flex flex-wrap items-center justify-between gap-2 cursor-pointer hover:bg-white/5 rounded-md px-3 -mx-3 transition-colors"
                          data-testid={`recent-enquiry-${e.id}`}
                          title="Click to open this enquiry"
                        >
                          <div>
                            <p className="text-sm font-semibold">{e.name} {e.urgent && <span className="text-brand-crimson text-xs">• URGENT</span>}</p>
                            <p className="text-xs text-white/50">{e.service || "General enquiry"}</p>
                          </div>
                          <span className="text-xs px-2.5 py-1 rounded-full bg-brand-crimson/15 text-brand-crimson border border-brand-crimson/30 uppercase tracking-wide">{e.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </TabsContent>

          <TabsContent value="enquiries" className="mt-8"><EnquiriesTab focusId={focusEnquiryId} onFocused={() => setFocusEnquiryId(null)} /></TabsContent>
          <TabsContent value="invoices" className="mt-8"><BillingTab /></TabsContent>
          <TabsContent value="clients" className="mt-8"><ClientsTab /></TabsContent>
          <TabsContent value="calendar" className="mt-8"><EventsTab /></TabsContent>
          <TabsContent value="monthly" className="mt-8"><MonthlyTab /></TabsContent>
          <TabsContent value="dogs" className="mt-8"><DogsTab onViewTraining={(dogId) => { setTrainingDog(dogId); setActiveTab("training"); }} /></TabsContent>
          <TabsContent value="training" className="mt-8"><TrainingTab requestedDog={trainingDog} /></TabsContent>
          <TabsContent value="content" className="mt-8"><ContentTab /></TabsContent>
          <TabsContent value="blog" className="mt-8"><BlogTab /></TabsContent>
          <TabsContent value="social" className="mt-8"><SocialTab /></TabsContent>
          <TabsContent value="settings" className="mt-8"><SettingsTab /></TabsContent>
          <TabsContent value="users" className="mt-8"><UsersTab /></TabsContent>
        </Tabs>
      </div>
    </main>
  );
}
